// DivisionenTab.jsx — Turnier-Divisionen verwalten
//
// Einzige Pflegestelle für die Divisionen EINES Turniers (V14, 18.10.2026):
// Auswahl aus dem Katalog, eigene Divisionen anlegen, ändern und entfernen.
// Den Katalog für alle Turniere pflegt der Plattform-Admin im Dashboard
// „Divisionen“; die Schnellregistrierung legt über dieselbe Route an.
import React, { useState, useEffect, useCallback } from 'react';
import { statusText } from '../../lib/turnierStatus';
import { geschlechtSchluessel } from '../../lib/divisionsFilter';

const ADD_MODES = [
  { id: 'tda',     label: 'TDA Standard',        icon: '📋' },
  { id: 'custom',  label: 'Eigene anlegen',       icon: '✏️' },
  { id: 'kopieren',label: 'Von Turnier kopieren', icon: '📥' },
];

const GENDER_OPT  = ['', 'Male', 'Female', 'Mixed'];
const TEAM_OPT    = ['Solo', 'Team'];

const emptyCustom = {
  Division_Code: '', Division_Name: '', Division_Type: '',
  Gender: '', Age_from: '', Age_to: '',
  Minimun_Weight: '', Maximum_Weight: '',
  Skill_Level: '', Team: 'Solo',
};

export default function DivisionenTab({ turnierId, authHeader }) {
  // ── Aktuelle Auswahl ──────────────────────────────────────────
  const [selected,    setSelected]    = useState([]);
  const [loadingSel,  setLoadingSel]  = useState(true);

  // ── Modus-Tabs ───────────────────────────────────────────────
  const [addMode,     setAddMode]     = useState('tda');

  // ── TDA-Liste ────────────────────────────────────────────────
  const [tdaList,     setTdaList]     = useState([]);
  const [loadingTDA,  setLoadingTDA]  = useState(false);
  const [tdaSearch,   setTdaSearch]   = useState('');
  const [tdaType,     setTdaType]     = useState('');
  const [tdaGender,   setTdaGender]   = useState('');
  const [tdaChecked,  setTdaChecked]  = useState(new Set());

  // ── Eigene anlegen ───────────────────────────────────────────
  const [customForm,  setCustomForm]  = useState(emptyCustom);
  const [bearbeiteCode, setBearbeiteCode] = useState(null); // eigene Division in Bearbeitung

  // ── Von Turnier kopieren ─────────────────────────────────────
  const [turniere,    setTurniere]    = useState([]);
  const [qturnierId,  setQturnierId]  = useState('');
  const [vorschau,    setVorschau]    = useState([]);
  const [loadingVorschau, setLoadingVorschau] = useState(false);
  const [kopierChecked, setKopierChecked] = useState(new Set());

  // ── Feedback ─────────────────────────────────────────────────
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  // ── Ausgewählte Divisionen laden ─────────────────────────────
  const loadSelected = useCallback(async () => {
    if (!turnierId) return;
    setLoadingSel(true);
    try {
      const res = await fetch(`/api/turniere/${turnierId}/divisionen`, { headers: authHeader });
      const d   = await res.json();
      setSelected(d.data || []);
    } catch (e) { console.error(e); }
    finally { setLoadingSel(false); }
  }, [turnierId, authHeader]);

  useEffect(() => { loadSelected(); }, [loadSelected]);

  // ── TDA-Divisionen laden ─────────────────────────────────────
  useEffect(() => {
    if (addMode !== 'tda' || tdaList.length > 0) return;
    setLoadingTDA(true);
    // Nur der allgemeine Katalog, nicht die eigenen Divisionen anderer Turniere
    fetch('/api/divisionen?limit=2000&katalog=1', { headers: authHeader })
      .then(r => r.json())
      .then(d => setTdaList(d.data || []))
      .catch(console.error)
      .finally(() => setLoadingTDA(false));
  }, [addMode, authHeader, tdaList.length]);

  // ── Turniere für Kopieren ────────────────────────────────────
  useEffect(() => {
    if (addMode !== 'kopieren' || turniere.length > 0) return;
    fetch('/api/turniere?status=all', { headers: authHeader })
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.data || []);
        setTurniere(list.filter(t => t.turnier_id !== turnierId));
      })
      .catch(console.error);
  }, [addMode, authHeader, turniere.length, turnierId]);

  // ── Vorschau laden wenn Quell-Turnier gewählt ────────────────
  useEffect(() => {
    if (!qturnierId) { setVorschau([]); return; }
    setLoadingVorschau(true);
    fetch(`/api/turniere/${turnierId}/divisionen/vorschau/${qturnierId}`, { headers: authHeader })
      .then(r => r.json())
      .then(d => {
        const rows = d.data || [];
        setVorschau(rows);
        setKopierChecked(new Set(rows.map(r => r.division_code)));
      })
      .catch(console.error)
      .finally(() => setLoadingVorschau(false));
  }, [qturnierId, turnierId, authHeader]);

  // ── Bereits ausgewählt? ──────────────────────────────────────
  const selectedCodes = new Set(selected.map(s => s.division_code));

  // ── TDA-Liste: gefiltert ─────────────────────────────────────
  const tdaFiltered = tdaList.filter(d => {
    const q = tdaSearch.toLowerCase();
    if (q && !d.Division_Code.toLowerCase().includes(q) && !(d.Division_Name || '').toLowerCase().includes(q)) return false;
    if (tdaType   && d.Division_Type !== tdaType)   return false;
    // Nach Schlüssel, nicht nach Schreibweise: „Male “ (mit Leerzeichen) fiel
    // hier bis 23.10.2026 aus dem Filter „Male“, „Mixed“ traf „Male/Female“ nie.
    if (tdaGender && geschlechtSchluessel(d.Gender) !== geschlechtSchluessel(tdaGender)) return false;
    return true;
  });

  // ── TDA-Typen für Filter ─────────────────────────────────────
  const tdaTypes = [...new Set(tdaList.map(d => d.Division_Type).filter(Boolean))].sort();

  // ── Checkbox-Toggle ─────────────────────────────────────────
  const toggleTDA = (code) => setTdaChecked(prev => {
    const next = new Set(prev);
    next.has(code) ? next.delete(code) : next.add(code);
    return next;
  });

  const toggleKopier = (code) => setKopierChecked(prev => {
    const next = new Set(prev);
    next.has(code) ? next.delete(code) : next.add(code);
    return next;
  });

  // ── Aktionen ─────────────────────────────────────────────────
  const clearFeedback = () => { setError(''); setSuccess(''); };

  const handleAddTDA = async () => {
    const toAdd = [...tdaChecked].filter(c => !selectedCodes.has(c));
    if (!toAdd.length) return setError('Keine neuen Divisionen ausgewählt.');
    clearFeedback();
    setSaving(true);
    try {
      const res = await fetch(`/api/turniere/${turnierId}/divisionen`, {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({ codes: toAdd, quelle: 'tda' }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Fehler');
      setSuccess(`${d.added} Division(en) hinzugefügt.`);
      setTdaChecked(new Set());
      loadSelected();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleAddCustom = async () => {
    if (!customForm.Division_Code.trim()) return setError('Division-Code ist Pflicht.');
    if (!customForm.Division_Name.trim()) return setError('Division-Name ist Pflicht.');
    clearFeedback();
    setSaving(true);
    try {
      const res = bearbeiteCode
        ? await fetch(`/api/turniere/${turnierId}/divisionen/custom/${encodeURIComponent(bearbeiteCode)}`, {
          method: 'PUT', headers: authHeader, body: JSON.stringify(customForm),
        })
        : await fetch(`/api/turniere/${turnierId}/divisionen/custom`, {
          method: 'POST', headers: authHeader, body: JSON.stringify(customForm),
        });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Fehler');
      setSuccess(bearbeiteCode ? `Division ${bearbeiteCode} gespeichert.` : 'Division angelegt.');
      setCustomForm(emptyCustom);
      setBearbeiteCode(null);
      loadSelected();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleKopieren = async () => {
    if (!qturnierId) return setError('Bitte Quell-Turnier wählen.');
    const toAdd = [...kopierChecked].filter(c => !selectedCodes.has(c));
    if (!toAdd.length) return setError('Alle gewählten Divisionen sind bereits vorhanden.');
    clearFeedback();
    setSaving(true);
    try {
      const res = await fetch(`/api/turniere/${turnierId}/divisionen/kopieren/${qturnierId}`, {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({ codes: toAdd }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Fehler');
      setSuccess(`${d.copied} Division(en) kopiert.`);
      loadSelected();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  // fetch wirft bei 4xx nicht: Antwort prüfen, sonst sieht „Entfernen“ wie
  // erledigt aus, obwohl der Server wegen Anmeldungen abgelehnt hat.
  const entfernen = async (code) => {
    const res = await fetch(`/api/turniere/${turnierId}/divisionen/${encodeURIComponent(code)}`, {
      method: 'DELETE', headers: authHeader,
    });
    if (res.ok) return null;
    const d = await res.json().catch(() => ({}));
    return d.error || `${code}: Fehler ${res.status}`;
  };

  const handleRemove = async (code) => {
    clearFeedback();
    try {
      const f = await entfernen(code);
      if (f) setError(f); else setSuccess(`${code} entfernt.`);
      loadSelected();
    } catch (e) { setError(e.message); }
  };

  const handleAlleEntfernen = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Alle ${selected.length} Divisionen aus diesem Turnier nehmen?\n\nDivisionen mit Anmeldungen bleiben stehen.`)) return;
    clearFeedback();
    setSaving(true);
    const fehler = [];
    for (const d of selected) {
      try { const f = await entfernen(d.division_code); if (f) fehler.push(f); }
      catch (e) { fehler.push(`${d.division_code}: ${e.message}`); }
    }
    setSaving(false);
    if (fehler.length) setError(`${selected.length - fehler.length} entfernt, ${fehler.length} nicht: ${fehler.slice(0, 3).join(' · ')}${fehler.length > 3 ? ' …' : ''}`);
    else setSuccess('Alle Divisionen entfernt.');
    loadSelected();
  };

  const handleBearbeiten = (d) => {
    clearFeedback();
    setBearbeiteCode(d.division_code);
    setCustomForm({
      Division_Code: d.division_code, Division_Name: d.Division_Name || '', Division_Type: d.Division_Type || '',
      Gender: d.Gender || '', Age_from: d.Age_from ?? '', Age_to: d.Age_to ?? '',
      Minimun_Weight: d.Minimun_Weight ?? '', Maximum_Weight: d.Maximum_Weight ?? '',
      Skill_Level: d.Skill_Level || '', Team: d.Team || 'Solo',
    });
    setAddMode('custom');
  };

  const setCustomField = (key, val) => setCustomForm(f => ({ ...f, [key]: val }));

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="tbe-section">
      {/* Header */}
      <div className="tbe-codes-header">
        <div>
          <div className="tbe-card-title" style={{ fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.2rem' }}>
            Divisionen
          </div>
          <div className="tbe-hint">
            Hier werden die Divisionen dieses Turniers gepflegt: aus dem TDA-Katalog wählen, eigene anlegen und ändern oder von einem anderen Turnier übernehmen. Den Katalog für alle Turniere pflegt der Plattform-Admin unter Dashboard → Divisionen.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="tbe-code-chip" style={{ fontSize: '0.85rem', padding: '0.3rem 0.75rem' }}>
            {selected.length} ausgewählt
          </div>
          {selected.length > 0 && (
            <button type="button" className="tbe-btn-cancel" onClick={handleAlleEntfernen} disabled={saving}>
              Alle entfernen
            </button>
          )}
        </div>
      </div>

      {!loadingSel && !selected.some(d => Number(d.in_auswahl ?? 1)) && (
        <div className="tbe-msg tbe-msg--info">
          Noch keine Auswahl aus dem Katalog: bei der Anmeldung stehen deshalb alle Katalog-Divisionen zur Wahl{selected.length ? ', dazu die eigenen unten' : ''}.
        </div>
      )}
      {success && <div className="tbe-msg tbe-msg--ok">{success}</div>}
      {error   && <div className="tbe-msg tbe-msg--err">{error}</div>}

      {/* Aktuelle Auswahl */}
      {loadingSel ? (
        <div className="tbe-hint" style={{ padding: '1rem' }}>Lade…</div>
      ) : selected.length === 0 ? (
        <div className="tbe-hint-empty">Noch keine Divisionen für dieses Turnier.</div>
      ) : (
        <div className="tbe-table-wrap" style={{ marginBottom: '1.5rem' }}>
          <table className="tbe-codes-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Typ</th>
                <th>Geschl.</th>
                <th>Alter</th>
                <th>Gewicht</th>
                <th>Quelle</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {selected.map(d => (
                <tr key={d.division_code}>
                  <td><span className="tbe-code-chip" style={{ fontSize: '0.75rem' }}>{d.division_code}</span></td>
                  <td style={{ fontSize: '0.82rem' }}>{d.Division_Name || '—'}</td>
                  <td style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>{d.Division_Type || '—'}</td>
                  <td style={{ fontSize: '0.75rem' }}>{d.Gender || '—'}</td>
                  <td style={{ fontSize: '0.75rem' }}>
                    {d.Age_from || d.Age_to ? `${d.Age_from ?? '?'}–${d.Age_to ?? '?'}` : '—'}
                  </td>
                  <td style={{ fontSize: '0.75rem' }}>
                    {d.Minimun_Weight || d.Maximum_Weight ? `${d.Minimun_Weight ?? '?'}–${d.Maximum_Weight ?? '?'}` : '—'}
                  </td>
                  <td>
                    <span style={{ fontSize: '0.7rem', opacity: 0.55 }}>
                      {Number(d.eigene) ? '✏️ Eigen' : d.quelle === 'kopiert' ? '📥 Kopiert' : '📋 TDA'}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {!!Number(d.eigene) && (
                      <button
                        type="button"
                        className="tbe-btn-icon"
                        onClick={() => handleBearbeiten(d)}
                        title="Bearbeiten"
                        aria-label={`${d.division_code} bearbeiten`}
                      >✎</button>
                    )}
                    <button
                      type="button"
                      className="tbe-btn-icon tbe-btn-icon--del"
                      onClick={() => handleRemove(d.division_code)}
                      title="Entfernen"
                    >✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Hinzufügen — Modus-Auswahl */}
      <div className="tbe-card" style={{ marginTop: '0.5rem' }}>
        <div className="tbe-card-title" style={{ marginBottom: '0.75rem' }}>Divisionen hinzufügen</div>
        <div className="div-mode-tabs">
          {ADD_MODES.map(m => (
            <button
              key={m.id}
              type="button"
              className={`div-mode-tab${addMode === m.id ? ' div-mode-tab--active' : ''}`}
              onClick={() => { setAddMode(m.id); clearFeedback(); }}
            >
              {m.icon} {m.label}
            </button>
          ))}
        </div>

        {/* ── TDA Standard ── */}
        {addMode === 'tda' && (
          <div className="div-tda-panel">
            <div className="div-filter-row">
              <input
                type="text"
                className="div-search"
                placeholder="Code oder Name suchen…"
                value={tdaSearch}
                onChange={e => setTdaSearch(e.target.value)}
              />
              <select value={tdaType} onChange={e => setTdaType(e.target.value)} className="div-select">
                <option value="">Alle Typen</option>
                {tdaTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={tdaGender} onChange={e => setTdaGender(e.target.value)} className="div-select">
                <option value="">Alle Geschl.</option>
                {GENDER_OPT.filter(Boolean).map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>

            {loadingTDA ? (
              <div className="tbe-hint" style={{ padding: '0.5rem' }}>Lade TDA-Divisionen…</div>
            ) : (
              <>
                <div className="div-list-header">
                  <span className="tbe-hint">
                    {tdaFiltered.length} Divisionen
                    {tdaChecked.size > 0 && ` · ${tdaChecked.size} gewählt`}
                  </span>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button type="button" className="tbe-btn-cancel" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                      onClick={() => setTdaChecked(new Set(tdaFiltered.filter(d => !selectedCodes.has(d.Division_Code)).map(d => d.Division_Code)))}>
                      Alle wählen
                    </button>
                    <button type="button" className="tbe-btn-cancel" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                      onClick={() => setTdaChecked(new Set())}>
                      Keine
                    </button>
                  </div>
                </div>
                <div className="div-list">
                  {tdaFiltered.map(d => {
                    const already = selectedCodes.has(d.Division_Code);
                    return (
                      <label
                        key={d.Division_Code}
                        className={`div-list-item${already ? ' div-list-item--already' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={already || tdaChecked.has(d.Division_Code)}
                          disabled={already}
                          onChange={() => !already && toggleTDA(d.Division_Code)}
                        />
                        <span className="div-item-code">{d.Division_Code}</span>
                        <span className="div-item-name">{d.Division_Name}</span>
                        <span className="div-item-meta">
                          {[d.Division_Type, d.Gender, d.Age_from && `${d.Age_from}–${d.Age_to}`]
                            .filter(Boolean).join(' · ')}
                        </span>
                        {already && <span className="div-item-already">✓</span>}
                      </label>
                    );
                  })}
                </div>
                <div style={{ marginTop: '0.75rem' }}>
                  <button
                    type="button"
                    className="tbe-btn-primary"
                    onClick={handleAddTDA}
                    disabled={saving || tdaChecked.size === 0}
                  >
                    {saving ? 'Hinzufügen…' : `${tdaChecked.size > 0 ? tdaChecked.size + ' ' : ''}Hinzufügen`}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Eigene anlegen ── */}
        {addMode === 'custom' && (
          <div className="div-custom-panel">
            <div className="tbe-grid">
              <div className="tbe-field">
                <label>Division-Code *</label>
                <input
                  type="text"
                  value={customForm.Division_Code}
                  onChange={e => setCustomField('Division_Code', e.target.value.toUpperCase())}
                  placeholder="z.B. MEINE-DIV-01"
                  style={{ letterSpacing: '0.08em' }}
                  disabled={!!bearbeiteCode}
                />
                <span className="tbe-hint">{bearbeiteCode ? 'Der Code bleibt beim Bearbeiten gleich' : 'Eindeutiger Bezeichner, wird automatisch groß'}</span>
              </div>
              <div className="tbe-field">
                <label>Name *</label>
                <input
                  type="text"
                  value={customForm.Division_Name}
                  onChange={e => setCustomField('Division_Name', e.target.value)}
                  placeholder="z.B. Anfänger Kata Männer"
                />
              </div>
              <div className="tbe-field">
                <label>Typ</label>
                <input
                  type="text"
                  value={customForm.Division_Type}
                  onChange={e => setCustomField('Division_Type', e.target.value)}
                  placeholder="z.B. Formen, Kumite…"
                />
              </div>
              <div className="tbe-field">
                <label>Geschlecht</label>
                <select value={customForm.Gender} onChange={e => setCustomField('Gender', e.target.value)}>
                  <option value="">— wählen —</option>
                  {GENDER_OPT.filter(Boolean).map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div className="tbe-field">
                <label>Alter von</label>
                <input type="number" min="0" max="100" value={customForm.Age_from}
                  onChange={e => setCustomField('Age_from', e.target.value)} placeholder="z.B. 8" />
              </div>
              <div className="tbe-field">
                <label>Alter bis</label>
                <input type="number" min="0" max="100" value={customForm.Age_to}
                  onChange={e => setCustomField('Age_to', e.target.value)} placeholder="z.B. 12" />
              </div>
              <div className="tbe-field">
                <label>Gewicht von (kg)</label>
                <input type="number" min="0" step="0.5" value={customForm.Minimun_Weight}
                  onChange={e => setCustomField('Minimun_Weight', e.target.value)} placeholder="z.B. 50" />
              </div>
              <div className="tbe-field">
                <label>Gewicht bis (kg)</label>
                <input type="number" min="0" step="0.5" value={customForm.Maximum_Weight}
                  onChange={e => setCustomField('Maximum_Weight', e.target.value)} placeholder="z.B. 60" />
              </div>
              <div className="tbe-field">
                <label>Skill-Level</label>
                <input type="text" value={customForm.Skill_Level}
                  onChange={e => setCustomField('Skill_Level', e.target.value)}
                  placeholder="z.B. Beginner, Advanced…" />
              </div>
              <div className="tbe-field">
                <label>Modus</label>
                <select value={customForm.Team} onChange={e => setCustomField('Team', e.target.value)}>
                  {TEAM_OPT.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginTop: '0.75rem' }}>
              <button type="button" className="tbe-btn-primary" onClick={handleAddCustom} disabled={saving}>
                {saving ? 'Speichern…' : bearbeiteCode ? 'Änderungen speichern' : 'Division anlegen'}
              </button>
              {bearbeiteCode && (
                <button type="button" className="tbe-btn-cancel" style={{ marginLeft: '0.5rem' }}
                  onClick={() => { setBearbeiteCode(null); setCustomForm(emptyCustom); }}>
                  Abbrechen
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Von Turnier kopieren ── */}
        {addMode === 'kopieren' && (
          <div className="div-kopieren-panel">
            <div className="tbe-field" style={{ maxWidth: '380px', marginBottom: '1rem' }}>
              <label>Quell-Turnier</label>
              <select value={qturnierId} onChange={e => setQturnierId(e.target.value)}>
                <option value="">— Turnier wählen —</option>
                {turniere.map(t => (
                  <option key={t.turnier_id} value={t.turnier_id}>
                    {t.name} ({statusText(t.status)})
                  </option>
                ))}
              </select>
            </div>

            {loadingVorschau && <div className="tbe-hint" style={{ padding: '0.5rem' }}>Lade Vorschau…</div>}

            {!loadingVorschau && vorschau.length > 0 && (
              <>
                <div className="div-list-header">
                  <span className="tbe-hint">
                    {vorschau.length} Divisionen im Quell-Turnier
                    {kopierChecked.size > 0 && ` · ${kopierChecked.size} gewählt`}
                  </span>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button type="button" className="tbe-btn-cancel" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                      onClick={() => setKopierChecked(new Set(vorschau.map(d => d.division_code)))}>
                      Alle
                    </button>
                    <button type="button" className="tbe-btn-cancel" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                      onClick={() => setKopierChecked(new Set())}>
                      Keine
                    </button>
                  </div>
                </div>
                <div className="div-list">
                  {vorschau.map(d => {
                    const already = selectedCodes.has(d.division_code);
                    return (
                      <label key={d.division_code} className={`div-list-item${already ? ' div-list-item--already' : ''}`}>
                        <input
                          type="checkbox"
                          checked={already || kopierChecked.has(d.division_code)}
                          disabled={already}
                          onChange={() => !already && toggleKopier(d.division_code)}
                        />
                        <span className="div-item-code">{d.division_code}</span>
                        <span className="div-item-name">{d.Division_Name || '—'}</span>
                        <span className="div-item-meta">
                          {[d.Division_Type, d.Gender, d.Age_from && `${d.Age_from}–${d.Age_to}`]
                            .filter(Boolean).join(' · ')}
                        </span>
                        {already && <span className="div-item-already">✓</span>}
                        {d.quelle === 'custom' && <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>✏️</span>}
                      </label>
                    );
                  })}
                </div>
                <div style={{ marginTop: '0.75rem' }}>
                  <button
                    type="button"
                    className="tbe-btn-primary"
                    onClick={handleKopieren}
                    disabled={saving || kopierChecked.size === 0}
                  >
                    {saving ? 'Kopieren…' : `${kopierChecked.size > 0 ? kopierChecked.size + ' ' : ''}Divisionen kopieren`}
                  </button>
                </div>
              </>
            )}

            {!loadingVorschau && qturnierId && vorschau.length === 0 && (
              <div className="tbe-hint-empty">Dieses Turnier hat keine Divisionen.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
